// pages/Ec2LogsTab.jsx — EC2 Health → instance modal → "Logs" tab (admin only)
// Backend: GET /api/ec2-health/logs/presets, POST /api/ec2-health/logs/fetch  (backend/ec2Logs.js)
// Read-only presets (security audit, troubleshooting, database, boot). Every fetch is written to
// the Console Log audit; the output itself is not stored anywhere.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../context/AuthContext';

const GROUPS = [
  ['security', 'Security audit'],
  ['troubleshoot', 'Troubleshooting'],
  ['database', 'Database & SAP'],
  ['boot', 'Boot / hang'],
];
const HOUR_LABEL = { 1: 'Last 1 hour', 6: 'Last 6 hours', 24: 'Last 24 hours', 72: 'Last 3 days', 168: 'Last 7 days' };
const LINE_OPTS = [100, 200, 500, 1000];
const VIA = { ssm: 'SSM Run Command', ssh: 'SSH', 'ec2-api': 'EC2 API' };
const BAD = /\b(error|errors|fail|failed|failure|fatal|panic|critical|denied|invalid user|oom|out of memory|killed process|segfault|deadlock|infected|locked out|unexpected|aborted?|corrupt\w*|i\/o error|call trace)\b/i;
const WARN = /\b(warn|warning|timeout|timed out|retry|refused|disconnect\w*)\b/i;
let presetCache = null;

function toSections(text) {
  const out = [];
  let cur = { title: null, lines: [] };
  String(text || '').split('\n').forEach((l) => {
    if (l.startsWith('### ')) { out.push(cur); cur = { title: l.slice(4), lines: [] }; } else cur.lines.push(l);
  });
  out.push(cur);
  return out.map((s) => {
    const lines = s.lines.slice();
    while (lines.length && !lines[0].trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    return { title: s.title, lines };
  }).filter((s) => s.title || s.lines.length);
}

function lineClass(l) {
  if (/^\(nothing found\)|^\(none|^\(empty|^\(no /.test(l)) return 'text-gray-500 italic';
  if (BAD.test(l)) return 'text-red-300';
  if (WARN.test(l)) return 'text-amber-200';
  return 'text-gray-200';
}

export default function Ec2LogsTab({ x, account, sshTarget }) {
  const [presets, setPresets] = useState(presetCache);
  const [pErr, setPErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [lines, setLines] = useState(200);
  const [hours, setHours] = useState(24);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [newestFirst, setNewestFirst] = useState(true);
  const [copied, setCopied] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (presetCache) return;
    let off = false;
    api.get('/api/ec2-health/logs/presets')
      .then((r) => { presetCache = r.data.presets || []; if (!off) setPresets(presetCache); })
      .catch((e) => { if (!off) setPErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { off = true; };
  }, []);
  useEffect(() => { reqId.current++; setRes(null); setErr(null); setBusy(false); }, [x.id]);

  const available = useMemo(() => (presets || []).filter((p) => p.os === 'any' || (x.windows ? p.os === 'windows' : p.os === 'linux')), [presets, x.windows]);
  useEffect(() => { if (sel && !available.find((p) => p.id === sel)) setSel(null); }, [available, sel]);
  const remoteOk = x.state === 'running' && (x.ssmPing === 'Online' || (!x.windows && !!sshTarget));
  const remoteWhy = x.state !== 'running' ? `Instance is ${x.state}`
    : x.windows ? `SSM agent ${x.ssmPing || 'not registered'} (Windows needs SSM Online)`
      : `SSM agent ${x.ssmPing || 'not registered'} and no SSH target for ${x.privateIp || 'this IP'}`;
  const filterOk = /^[A-Za-z0-9 _.:@=#/()[\]-]{0,80}$/.test(filter);
  const preset = available.find((p) => p.id === sel) || null;

  const run = async (id) => {
    const pid = id || sel;
    if (!pid || !filterOk) return;
    setSel(pid);
    const my = ++reqId.current;
    setBusy(true); setErr(null); setRes(null);
    try {
      const r = await api.post('/api/ec2-health/logs/fetch', { account, instanceId: x.id, preset: pid, lines, hours, filter: filter.trim() });
      if (my === reqId.current) setRes(r.data);
    } catch (e) {
      if (my === reqId.current) setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { if (my === reqId.current) setBusy(false); }
  };

  const sections = useMemo(() => {
    const s = toSections(res && res.text);
    return newestFirst ? s : s.map((q) => ({ title: q.title, lines: q.lines.slice().reverse() }));
  }, [res, newestFirst]);
  const plain = useMemo(() => sections.map((s) => (s.title ? `### ${s.title}\n` : '') + s.lines.join('\n')).join('\n\n'), [sections]);
  const fileName = () => `${(x.name || x.id).replace(/[^\w.-]+/g, '_')}-${res ? res.preset : 'log'}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.log`;
  const copy = () => { navigator.clipboard && navigator.clipboard.writeText(plain).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  const download = () => {
    const url = URL.createObjectURL(new Blob([plain + '\n'], { type: 'text/plain' }));
    const a = document.createElement('a'); a.href = url; a.download = fileName(); a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (pErr) return <div className="text-sm text-red-600">{pErr}</div>;
  if (!presets) return <div className="text-sm text-gray-400">Loading log presets…</div>;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <label className="text-xs text-gray-500">
          <div className="mb-1">Range</div>
          <select value={hours} onChange={(e) => setHours(+e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            {Object.keys(HOUR_LABEL).map((h) => <option key={h} value={h}>{HOUR_LABEL[h]}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500">
          <div className="mb-1">Max lines / section</div>
          <select value={lines} onChange={(e) => setLines(+e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            {LINE_OPTS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="min-w-[200px] flex-1 text-xs text-gray-500">
          <div className="mb-1">Contains (optional)</div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            placeholder="e.g. p01adm, 10.32.18.13, Error: 1105"
            className={`w-full rounded-lg border px-2 py-1.5 text-sm ${filterOk ? 'border-gray-300' : 'border-red-400 bg-red-50'}`} />
        </label>
        <button type="button" onClick={() => run()} disabled={!sel || busy || !filterOk || (sel !== 'console' && !remoteOk)}
          className="rounded-lg bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-40">
          {busy ? 'Fetching…' : res ? 'Refresh' : 'Fetch'}
        </button>
        {!filterOk && <div className="w-full text-[11px] text-red-600">Filter: letters, digits, space and . _ : @ = # / ( ) [ ] - only (max 80).</div>}
      </div>

      {!remoteOk && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {remoteWhy}. Only <b>EC2 console output</b> is available for this instance.
        </div>
      )}

      {/* Presets */}
      <div className="grid gap-3 md:grid-cols-2">
        {GROUPS.map(([g, label]) => {
          const ps = available.filter((p) => p.group === g);
          if (!ps.length) return null;
          return (
            <div key={g} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
              <div className="flex flex-wrap gap-1.5">
                {ps.map((p) => {
                  const dis = busy || (p.id !== 'console' && !remoteOk);
                  return (
                    <button key={p.id} type="button" title={p.desc} disabled={dis} onClick={() => run(p.id)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium ring-1 disabled:opacity-40 ${sel === p.id ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white text-gray-700 ring-gray-200 hover:bg-sky-50 hover:ring-sky-200'}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {preset && (
        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-700">{preset.label}:</span> {preset.desc}
          {preset.time === 'no' && <span className="ml-1 text-gray-400">(Range does not apply.)</span>}
          {preset.time === 'partial' && <span className="ml-1 text-gray-400">(Range applies to lines with a recognisable timestamp.)</span>}
        </div>
      )}

      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {busy && <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-400">Running read-only probe on {x.name}… (SSM can take 10–40 s)</div>}

      {res && !busy && (
        <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-800 px-3 py-2 text-[11px] text-gray-400">
            <span>via {VIA[res.via] || res.via}</span>
            <span>· {(res.ms / 1000).toFixed(1)} s</span>
            <span>· {res.preset === 'console' ? 'console' : HOUR_LABEL[res.hours]}</span>
            {res.filter && <span>· contains “{res.filter}”</span>}
            <span>· {new Date(res.fetchedAt).toLocaleTimeString()}</span>
            <div className="ml-auto flex gap-1">
              <button type="button" onClick={() => setNewestFirst((v) => !v)} className="rounded px-2 py-0.5 text-gray-300 ring-1 ring-gray-700 hover:bg-gray-800">
                {newestFirst ? 'Newest first' : 'Oldest first'}
              </button>
              <button type="button" onClick={copy} className="rounded px-2 py-0.5 text-gray-300 ring-1 ring-gray-700 hover:bg-gray-800">{copied ? 'Copied' : 'Copy'}</button>
              <button type="button" onClick={download} className="rounded px-2 py-0.5 text-gray-300 ring-1 ring-gray-700 hover:bg-gray-800">Download .log</button>
            </div>
          </div>
          {res.truncated && (
            <div className="bg-amber-900/40 px-3 py-1.5 text-[11px] text-amber-200">
              Output hit the SSM output limit, so the oldest lines of the last section were cut. Lower “Max lines” or add a filter.
            </div>
          )}
          <div className="max-h-[60vh] overflow-auto px-3 py-2 font-mono text-[11.5px] leading-[1.45]">
            {sections.length === 0 && <div className="italic text-gray-500">(empty output)</div>}
            {sections.map((s, i) => (
              <div key={i} className="mb-3">
                {s.title && <div className="sticky top-0 -mx-3 mb-1 bg-gray-900/95 px-3 py-1 font-semibold text-sky-300">{s.title}</div>}
                {s.lines.map((l, j) => <div key={j} className={`whitespace-pre ${lineClass(l)}`}>{l || ' '}</div>)}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        Read-only presets; nothing is changed on the instance. Each fetch is recorded in File Deploy → Console Log (kind <span className="font-mono">log-fetch</span>); the output is not stored.
      </p>
    </div>
  );
}
